import React, { useEffect, useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { ReorderHandle } from 'screens/oneOnOneRabbit/InstancePage/features/Agenda/AgendaList';
import { setAgendaLibraryIsOpen } from 'screens/oneOnOneRabbit/InstancePage/slice/OneOnOneInstancePageSlice';
import { AGENDA_CONDITIONS_TYPE } from 'screens/oneOnOneRabbit/SettingsPage/constants';
import { OneOnOneSettingsPageReactQueryKeys } from 'screens/oneOnOneRabbit/SettingsPage/constants/OneOnOneSettingsPageReactQueryKeys';
import { AdminAgenda } from 'screens/oneOnOneRabbit/SettingsPage/interfaces/AdminAgenda';
import { setSaveButtonIsDisabled } from 'screens/oneOnOneRabbit/SettingsPage/slice/OneOnOneEventBasedAgendaSlice';
import { z } from 'zod';

import { Plus } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EventTemplateAgendaType } from '../services';
import { AdminAgendaItem } from './AdminAgendaItem';
import { CreateAdminAgenda } from './CreateAdminAgenda';

type agenda = z.infer<typeof AdminAgenda>;

interface Props {
  agenda: agenda[];
  agendaCondition: AGENDA_CONDITIONS_TYPE;
}

const reorder = (list: agenda[], startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const AgendaList = ({ agenda, agendaCondition }: Props) => {
  const [creatingAgenda, setCreatingAgenda] = useState(false);

  // react beautiful dnd needs state to be updated in a sync manner that's why had to use useState instead of useMemo
  const [userCreatedAgendaItemsSorted, setUserCreatedAgendaItemsSorted] = useState(agenda);

  useEffect(() => {
    setUserCreatedAgendaItemsSorted(agenda);
  }, [agenda]);

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const reorderedAgenda = reorder(agenda, source.index, destination.index);

    const reorderedAgendaWithDisplayOrder = reorderedAgenda.map((agenda, index) => ({
      ...agenda,
      displayOrder: index + 1,
    }));

    setUserCreatedAgendaItemsSorted(reorderedAgendaWithDisplayOrder);

    const isRegularAgenda = agendaCondition === 'REGULAR';

    if (isRegularAgenda) {
      queryClient.setQueryData<agenda[]>(
        [OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'REGULAR'],
        reorderedAgendaWithDisplayOrder
      );
    } else {
      queryClient.setQueryData<EventTemplateAgendaType>(
        [OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'EVENT_BASED'],
        (oldData) => ({
          ...oldData,
          [agendaCondition]: reorderedAgendaWithDisplayOrder,
        })
      );
    }
    dispatch(
      setSaveButtonIsDisabled({
        saveButtonIsDisabled: false,
      })
    );
  };

  const addAgendaButtonOptions = useMemo(
    () => [
      {
        value: 'Add Agenda Point',
        label: 'Add agenda point',
        icon: null,
        onClick: () => {
          setCreatingAgenda(true);
          dispatch(
            setSaveButtonIsDisabled({
              saveButtonIsDisabled: false,
            })
          );
        },
      },
      {
        value: 'Suggested Agenda Point',
        label: 'Suggested Agenda Point',
        icon: null,
        onClick: () => {
          dispatch(
            setAgendaLibraryIsOpen({
              isOpen: true,
              agendaCondition,
            })
          );
        },
      },
    ],
    []
  );

  return (
    <Box>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, parentSnapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {userCreatedAgendaItemsSorted
                ?.filter(({ isDeleted }) => !isDeleted)
                ?.map((agenda, index) => (
                  <Draggable key={index} draggableId={`draggable-${index}`} index={index}>
                    {(provided, snapshot) => (
                      <div {...provided.draggableProps} ref={provided.innerRef}>
                        <Box
                          sx={{
                            '&:hover .reorder-handle': {
                              visibility: 'visible',
                            },
                            pointerEvents: parentSnapshot.isDraggingOver ? 'none' : 'auto',
                          }}
                          position="relative"
                          display="flex"
                          w="100%"
                          alignItems="center"
                        >
                          <ReorderHandle
                            isDragging={snapshot.isDragging}
                            // @ts-ignore
                            left="-4px"
                            {...provided.dragHandleProps}
                          />
                          <AdminAgendaItem
                            isDragging={snapshot.isDragging}
                            isDraggingOver={parentSnapshot.isDraggingOver}
                            key={agenda?.id ?? agenda?.uiId}
                            uiId={agenda?.uiId}
                            {...agenda}
                          />
                        </Box>
                        {/* <Divider opacity={1} color="#EBEFF3" /> */}
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {creatingAgenda && <CreateAdminAgenda agendaCondition={agendaCondition} setCreatingAgenda={setCreatingAgenda} />}

      <Flex h="72px" alignItems="center">
        {/* @ts-ignore */}
        <Button
          ml="4px"
          variant="tertiary"
          StartIcon={Plus}
          onClick={() => {
            setCreatingAgenda(true);
            dispatch(
              setSaveButtonIsDisabled({
                saveButtonIsDisabled: false,
              })
            );
          }}
        >
          Add agenda point
        </Button>
        <TextBodyMedium bold>Or</TextBodyMedium>
        <Button
          variant="tertiary"
          onClick={() => {
            dispatch(
              setAgendaLibraryIsOpen({
                isOpen: true,
                agendaCondition,
              })
            );
          }}
        >
          Add from library
        </Button>
      </Flex>
    </Box>
  );
};

export { AgendaList };
